import { env } from '@/lib/env';

/**
 * The tutoring platform's URLs, as Flutter's `admin-app/lib/src/constants.dart` builds them.
 *
 * A parent, student or tutor's account lives on the platform (`VITE_PLATFORM_ORIGIN`), not on
 * the teacher's app (`VITE_WEB_ORIGIN`) — whose prod origin is `tutero.ai`, so an account sent
 * there lands on Tutero AI (ISSUE-25625). Only the View as tool (`src/tools/view-as/`), whose
 * subject is a teacher, names the teacher's app.
 */

/** Whose platform is opened; the query key is Flutter's `emulatedLabel()`. */
export type PlatformRole = 'parent' | 'student' | 'tutor';

const EMULATED_PARAM: Record<PlatformRole, string> = {
  parent: 'emulatedParent',
  student: 'emulatedStudent',
  tutor: 'emulatedTutor',
};

/** An account whose platform a surface can open: the id, and whose it is. */
export interface PlatformAccount {
  id: string;
  as: PlatformRole;
}

/**
 * The platform as an account, in a new tab (Flutter's `emulateUser`): `/loading` with the
 * role's own `emulated*` query key, which the tutoring app's `UserIdsInterceptor` reads
 * (`tutoring-app/lib/src/presentation/components/layout/interceptors/user_ids_interceptor.dart`).
 * The role cannot be omitted — one origin with no role was the defect.
 */
export function viewAsUrl(id: string, as: PlatformRole): string {
  const url = new URL('/loading', env.VITE_PLATFORM_ORIGIN);
  url.searchParams.set(EMULATED_PARAM[as], id);
  return url.toString();
}

/** A tutor's public profile on the platform (Flutter's `tutorProfileUri`). */
export function tutorProfileUrl(tutorId: string): string {
  return new URL(
    `/tutors/${encodeURIComponent(tutorId)}`,
    env.VITE_PLATFORM_ORIGIN,
  ).toString();
}
